const validationLogicData = {
    folder: {
        delete: [
            {
                operation: 'emptyCollection',
                onFail: {
                    title: 'documents.folder.error.title',
                    msg: 'documents.folder.error.hasDocuments'
                },
                condition: {
                    type: 'collection',
                    collection: 'document',
                    filter: { field: 'folderId', value: 'id' }
                }
            },
            {
                operation: 'emptyCollection',
                onFail: {
                    title: 'documents.folder.error.title',
                    msg: 'documents.folder.error.hasChildren'
                },
                condition: {
                    type: 'collection',
                    collection: 'folder',
                    filter: { field: 'parentFolderId', value: 'id' }
                }
            }
        ]
    },
    // Purchases › Inbox (38.04): a used document is an expense's attachment —
    // deleting it would take the expense's file with it.
    inboxDocument: {
        delete: [
            {
                operation: 'checkField',
                onFail: {
                    title: 'purchases.inbox.delete.error.title',
                    msg: 'purchases.inbox.delete.error.used'
                },
                condition: { field: 'status', operator: 'notEquals', value: 'used' }
            }
        ]
    },
    // Reverse charge only for a customer whose EU VAT ID VIES has validated.
    // reverseChargeLines is derived by logicData from each line's VAT code;
    // a draft saved before it existed has none and is not blocked.
    invoice: {
        book: [
            {
                operation: 'checkField',
                onFail: {
                    title: 'invoice.book.error.title',
                    msg: 'invoice.book.error.reverseChargeVatId'
                },
                condition: {
                    any: [
                        { field: 'vatIdStatus', operator: 'equals', value: 'valid' },
                        { field: 'reverseChargeLines', operator: 'notExists' },
                        { field: 'reverseChargeLines', operator: 'lte', value: 0 }
                    ]
                }
            }
        ]
    },
    stockShipment: {
        book: [
            {
                operation: 'compareSum',
                onFail:
                {
                    title: 'inventory.shipment.book.error.title',
                    msg: 'inventory.shipment.book.error.location',
                },
                target: {
                    type: "array",
                    array: "lines",
                    field: "itemQuantity"
                },
                condition: {
                    type: 'subArray',
                    array: 'lines',
                    subArray: 'locations',
                    field: 'quantity'
                },
            },
            {
                operation: 'compareSum',
                onFail: {
                    title: 'inventory.shipment.book.error.title',
                    msg: 'inventory.shipment.book.error.reservation',
                },
                target: {
                    type: "array",
                    array: "lines",
                    field: "itemQuantity"
                },
                condition: {
                    type: 'array',
                    array: 'reservations',
                    field: 'quantity'
                },
            },

        ],
        delete: [
            {
                operation: 'emptyArray',
                onFail:
                {
                    msg: 'inventory.shipment.delete.error.location',
                    title: 'inventory.shipment.delete.error.title',
                },
                condition: {
                    type: 'array',
                    array: 'reservations',
                },
            }
        ]
    },
    product: {
        delete: [
            {
                operation: 'emptySubCollection',
                onFail: {
                    msg: 'product.delete.error.location',
                    title: 'product.delete.error.title',
                },
                condition: {
                    collection: 'product',
                    subCollection: 'itemLocation',
                }
            }
        ]
    },
    // ── Location hierarchy: warehouse → hall → rack → location ────────────────
    //
    // Every level asks the same two questions, and never "do I have children?":
    //
    //   1. does anything in my subtree still hold stock?
    //   2. is any open warehouse order routed through my subtree?
    //
    // Structure alone must not block a delete — an empty rack or hall is removed
    // together with its parent by the cascade in eventLogicData.js. Blocking on the
    // mere existence of children made the levels undeletable, because every rack,
    // hall and warehouse materialises its own footprint location on create.
    //
    // Both questions resolve against `location`, which denormalises warehouseId /
    // hallId / rackId, so each level costs the same two indexed queries no matter
    // how deep the subtree is. Reservations always sit on a location that holds
    // stock, so question 1 covers them everywhere above `location` itself.
    location: {
        delete: [
            {
                // Free — no query — and it short-circuits the lookups below.
                operation: 'checkField',
                onFail: {
                    title: 'location.delete.error.title',
                    msg: 'location.delete.error.inventory'
                },
                condition: { field: 'inventory', operator: 'empty' }
            },
            {
                operation: 'noReferences',
                onFail: {
                    title: 'location.delete.error.title',
                    msg: 'location.delete.error.reservation'
                },
                condition: {
                    type: 'collection',
                    collection: 'stockReservation',
                    match: { locationId: 'id' }
                }
            },
            {
                // Open warehouse orders routed through this location — header level or
                // any line. Narrowed to incomplete orders in the query, then matched
                // per row because the line references live inside a JSON array.
                operation: 'noReferences',
                onFail: {
                    title: 'location.delete.error.title',
                    msg: 'location.delete.error.order'
                },
                condition: {
                    type: 'collection',
                    collection: 'warehouseOrder',
                    filter: { completed: false },
                    where: [{
                        any: [
                            { field: 'fromLocationId', operator: 'equals', valueFrom: 'id' },
                            { field: 'toLocationId', operator: 'equals', valueFrom: 'id' },
                            { field: 'lines[].fromLocationId', operator: 'contains', valueFrom: 'id' },
                            { field: 'lines[].toLocationId', operator: 'contains', valueFrom: 'id' }
                        ]
                    }]
                }
            }
        ]
    },
    rack: {
        delete: [
            {
                operation: 'noReferences',
                onFail: {
                    title: 'rack.delete.error.title',
                    msg: 'rack.delete.error.inventory'
                },
                condition: {
                    type: 'collection',
                    collection: 'location',
                    match: { rackId: 'id' },
                    where: [{ field: 'inventory', operator: 'notEmpty' }]
                }
            },
            {
                operation: 'noReferences',
                onFail: {
                    title: 'rack.delete.error.title',
                    msg: 'rack.delete.error.order'
                },
                condition: {
                    type: 'collection',
                    collection: 'warehouseOrder',
                    filter: { completed: false },
                    via: { collection: 'location', match: { rackId: 'id' }, select: 'id' },
                    where: [{
                        any: [
                            { field: 'fromLocationId', operator: 'in', valueFrom: '_via' },
                            { field: 'toLocationId', operator: 'in', valueFrom: '_via' },
                            { field: 'lines[].fromLocationId', operator: 'containsAny', valueFrom: '_via' },
                            { field: 'lines[].toLocationId', operator: 'containsAny', valueFrom: '_via' }
                        ]
                    }]
                }
            }
        ]
    },
    hall: {
        delete: [
            {
                operation: 'noReferences',
                onFail: {
                    title: 'hall.delete.error.title',
                    msg: 'hall.delete.error.inventory'
                },
                condition: {
                    type: 'collection',
                    collection: 'location',
                    match: { hallId: 'id' },
                    where: [{ field: 'inventory', operator: 'notEmpty' }]
                }
            },
            {
                operation: 'noReferences',
                onFail: {
                    title: 'hall.delete.error.title',
                    msg: 'hall.delete.error.order'
                },
                condition: {
                    type: 'collection',
                    collection: 'warehouseOrder',
                    filter: { completed: false },
                    via: { collection: 'location', match: { hallId: 'id' }, select: 'id' },
                    where: [{
                        any: [
                            { field: 'fromLocationId', operator: 'in', valueFrom: '_via' },
                            { field: 'toLocationId', operator: 'in', valueFrom: '_via' },
                            { field: 'lines[].fromLocationId', operator: 'containsAny', valueFrom: '_via' },
                            { field: 'lines[].toLocationId', operator: 'containsAny', valueFrom: '_via' }
                        ]
                    }]
                }
            }
        ]
    },
    warehouse: {
        delete: [
            {
                operation: 'noReferences',
                onFail: {
                    title: 'warehouse.delete.error.title',
                    msg: 'warehouse.delete.error.inventory'
                },
                condition: {
                    type: 'collection',
                    collection: 'location',
                    match: { warehouseId: 'id' },
                    where: [{ field: 'inventory', operator: 'notEmpty' }]
                }
            },
            {
                // Warehouse orders carry warehouseId directly, so this needs no `via`.
                operation: 'noReferences',
                onFail: {
                    title: 'warehouse.delete.error.title',
                    msg: 'warehouse.delete.error.order'
                },
                condition: {
                    type: 'collection',
                    collection: 'warehouseOrder',
                    match: { warehouseId: 'id' },
                    filter: { completed: false }
                }
            }
        ]
    },
    paymentAccount: {
        delete: [{
            operation: 'emptySubCollection',
            onFail: {
                title: 'bank.account.delete.error.title',
                msg: 'bank.account.delete.error.transaction'
            },
            condition: {
                collection: 'paymentAccount',
                subCollection: 'paymentTransaction',
            }
        }]
    }

}

export default validationLogicData